import {memo} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconCheckSmall from '@adv-mt/mt-svg-icons/lib/CheckSmall.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Badge, BADGE_DESIGNS, BADGE_INTENTS, BADGE_SIZES} from '@adv-mt/ui/badge'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Icon, ICON_INTENTS, ICON_SIZES} from '@adv-mt/ui/icon'
import {Body1, Headline1} from '@adv-mt/ui/typography'

import {BASE_CLASS as PAGE_BASE_CLASS} from '../../config.js'
import {DRAWER_TYPES} from '../../drawers/config.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-rentingServices`
const MAX_SERVICES_DISPLAYED = 6

const RentingServices = ({onOpenDrawer, seller = {}}) => {
  const {domain, i18n} = useSuiContext()
  const {track: analyticsTrack} = useAnalyticsTrack()
  const {mainRentingServicesIncluded, services = []} = seller.dealer ?? {}
  const includedServices = services.filter(({isIncluded}) => isIncluded)

  const {HIT_ORIGINS, TRACKING_EVENT_NAMES} = domain.get('config').get('tracking')

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !domain.get('config').get('isMobile')
  })

  const handleShowAllServices = async () => {
    onOpenDrawer({data: services, type: DRAWER_TYPES.RENTING_SERVICES})

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.DEALER_SERVICES_POPUP,
      origin: HIT_ORIGINS.AD_DETAIL
    })

    analyticsTrack(eventData)
  }

  if (includedServices.length === 0) {
    return null
  }

  return (
    <section className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-header`}>
        <Headline1>{i18n.t('DEALER_SERVICE.DEALER_SERVICES_SUBSCRIPTION_TITLE')}</Headline1>
        {Boolean(mainRentingServicesIncluded) && (
          <Badge design={BADGE_DESIGNS.SOFT} intent={BADGE_INTENTS.SUCCESS} size={BADGE_SIZES.MEDIUM}>
            {i18n.t('DEALER_SERVICE.MAIN_SERVICES_INCLUDED')}
          </Badge>
        )}
      </div>

      <ul className={`${BASE_CLASS}-list`}>
        {includedServices.slice(0, MAX_SERVICES_DISPLAYED).map(({title, type}) => (
          <li className={`${BASE_CLASS}-item`} key={type}>
            <Icon size={ICON_SIZES.MD} intent={ICON_INTENTS.PRIMARY}>
              <IconCheckSmall size={ICON_SIZES.MD} />
            </Icon>
            <Body1>{title}</Body1>
          </li>
        ))}
      </ul>

      {services.length > MAX_SERVICES_DISPLAYED && (
        <div className={`${BASE_CLASS}-actions`}>
          <Button
            onClick={handleShowAllServices}
            shape={BUTTON_SHAPES.CIRCLE}
            size={isHigherThanMobile ? BUTTON_SIZES.MEDIUM : BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.OUTLINE}
          >
            {i18n.t('DEALER_SERVICE.SHOW_ALL_SERVICES_INCLUDED')}
          </Button>
        </div>
      )}
    </section>
  )
}

RentingServices.displayName = 'RentingServices'

RentingServices.propTypes = {
  onOpenDrawer: PropTypes.func.isRequired,
  seller: PropTypes.shape({
    dealer: PropTypes.shape({
      mainRentingServicesIncluded: PropTypes.bool,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          description: PropTypes.string,
          isIncluded: PropTypes.bool,
          title: PropTypes.string,
          type: PropTypes.string
        })
      )
    })
  })
}

export default memo(RentingServices)
